import React, { useId, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { colors, fontSize, margins } from "style";
import { PREPARE_INTERVIEW_SCHEDULING_REQUEST } from "graphql/mutations";
import { GET_APPLICANT_INTERVIEW_SCHEDULING } from "graphql/queries/interviewSchedulingSettings";
import { MUTATION_TYPE, useApplicantInterviewSettings } from "hooks";
import { ModalForm, MODAL_SIZE } from "components/Modal";
import { UserFullName, UserPhoto } from "components/User";
import { SIZE, TYPE } from "components/Form";
import { convertFromPlainText, getPlainText } from "components/Form/Editor";
import { INTERVIEW_EVENT_STATUS_MESSAGES } from "constants/index";
import ApplicantInterviewRequests from "./ApplicantInterviewRequests";

/**
 * ApplicantInterviewModal
 *
 * Prepares a new interview scheduling link with a rich-text message, above every request already sent.
 *
 * @param {Object}  application Applicant and role context
 * @param {Boolean} show        Whether the modal is open
 */
const ApplicantInterviewModal = ({ application, show, ...props }) => {
  const titleId = useId();
  const requestsTitleId = useId();
  const intl = useIntl();

  const [submitting, setSubmitting] = useState(false);

  const { setting, requests, loading, error } = useApplicantInterviewSettings({ show, applicationId: application.id });

  const hasLoaded = !loading && !error;
  const canInvite = Boolean(hasLoaded && setting?.ready);

  const initialValues = useMemo(() => {
    const message = intl.formatMessage(
      { id: "Missions.Applicants.InterviewModal.DefaultMessage" },
      {
        name:
          application.profile?.firstName ||
          intl.formatMessage({ id: "Missions.Applicants.InterviewModal.DefaultName" }),
        role:
          application.missionRole?.name || intl.formatMessage({ id: "Missions.Applicants.InterviewModal.DefaultRole" }),
      }
    );

    return {
      input: {
        missionRoleApplicantId: application.id,
        interviewSchedulingSettingId: setting?.id,
        message,
        messageJson: convertFromPlainText(message),
      },
    };
  }, [application, setting]);

  return (
    <ModalForm
      {...props}
      show={show}
      size={MODAL_SIZE.medium}
      title={
        <span id={titleId}>
          <FormattedMessage id="Missions.Applicants.InterviewModal.Title" />
        </span>
      }
      modalProps={{ "aria-labelledby": titleId, restoreFocusOptions: { preventScroll: true } }}
      primaryButtonMessageId="Missions.Applicants.InterviewModal.Prepare"
      secondaryButtonMessageId="Global.Cancel"
      showSave={canInvite}
      closeOnComplete={false}
      loading={submitting}
      disabled={submitting || !canInvite}
      data={canInvite ? getFormData(intl, submitting) : []}
      initialValues={initialValues}
      values={initialValues}
      mutationType={MUTATION_TYPE.add}
      mutationData={MUTATION_DATA}
      onLoadingState={setSubmitting}
      aside={
        <section css={styles.requests} aria-labelledby={requestsTitleId}>
          <h3 id={requestsTitleId} css={styles.requestsTitle}>
            <FormattedMessage id="Missions.Applicants.InterviewModal.Requests.Title" />
          </h3>
          <div css={styles.requestsList}>
            <ApplicantInterviewRequests requests={requests} loading={loading} error={error} />
          </div>
        </section>
      }
    >
      <div>
        <div css={styles.applicant}>
          <UserPhoto
            data={{
              firstName: application.profile?.firstName,
              lastName: application.profile?.lastName,
              avatarUrl: application.profile?.avatarUrl,
            }}
            width="4.8rem"
            height="4.8rem"
            borderRadius="100%"
          />
          <div>
            <div css={styles.name}>
              <UserFullName data={application.profile} disableLink />
            </div>
            <div css={styles.role}>{application.missionRole?.name}</div>
          </div>
        </div>

        {error && (
          <p role="alert" css={styles.error}>
            <FormattedMessage id="Missions.Applicants.InterviewModal.SettingsError" />
          </p>
        )}

        {hasLoaded && setting?.eventStatus && INTERVIEW_EVENT_STATUS_MESSAGES[setting.eventStatus] && (
          <p css={styles.notice} role="alert">
            <FormattedMessage id={INTERVIEW_EVENT_STATUS_MESSAGES[setting.eventStatus]} />
          </p>
        )}

        {hasLoaded && (
          <p css={canInvite ? styles.intro : styles.notice}>
            <FormattedMessage
              id={
                canInvite
                  ? "Missions.Applicants.InterviewModal.InviteHelp"
                  : "Missions.Applicants.InterviewModal.SetupRequired"
              }
            />
          </p>
        )}
      </div>
    </ModalForm>
  );
};

const MUTATION_DATA = {
  mutationName: { add: PREPARE_INTERVIEW_SCHEDULING_REQUEST },
  refetchQueries: [GET_APPLICANT_INTERVIEW_SCHEDULING],
  refetchAfterMutate: true,
  unWrapParams: true,
};

const getFormData = (intl, readOnly) => [
  {
    items: [
      {
        label: <FormattedMessage id="Missions.Applicants.InterviewModal.Message" />,
        type: TYPE.editor,
        size: SIZE.xlarge,
        parentObjectKey: "input",
        onChange: (name, value, handleOnChange) => {
          handleOnChange("message", getPlainText(value));
          handleOnChange(name, value);
        },
        properties: {
          name: "messageJson",
          showFormattingOptions: !readOnly,
          readOnly,
          editableProps: { "aria-label": intl.formatMessage({ id: "Missions.Applicants.InterviewModal.Message" }) },
        },
      },
    ],
  },
];

const styles = {
  applicant: css`
    display: flex;
    align-items: center;
    gap: 1.4rem;
    padding-bottom: ${margins.normal};
    margin-bottom: ${margins.normal};
    border-bottom: 1px solid ${colors.grayAnatomyLight3};
  `,
  name: css`
    font-size: ${fontSize.medium};
    font-weight: 600;
    color: ${colors.grayAnatomyDark};
  `,
  role: css`
    margin-top: 0.4rem;
    color: ${colors.grayAnatomyBase};
    overflow-wrap: anywhere;
  `,
  intro: css`
    color: ${colors.grayAnatomyBase};
    line-height: 1.7;
  `,
  notice: css`
    padding: 1.6rem;
    border-radius: 1rem;
    background: ${colors.purpleRainLight8};
    line-height: 1.7;
  `,
  error: css`
    color: ${colors.darkRed};
  `,
  requests: css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  requestsList: css`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  `,
  requestsTitle: css`
    margin: 0 0 1rem;
    font-size: ${fontSize.normal};
    font-weight: 600;
    color: ${colors.grayAnatomyDark};
  `,
};

ApplicantInterviewModal.propTypes = {
  application: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
};

export default ApplicantInterviewModal;
